import { useState, useEffect, useRef } from "react";
import api from "../lib/api";
import { Sparkles, RefreshCw, Send, X, Trash2, MessageSquare, AlertTriangle, TrendingUp, Award, Target, ChevronDown, ChevronUp, Rows3, Rows2 } from "lucide-react";

const TYPE_ICONS = {
  riesgo: <AlertTriangle size={16} />,
  oportunidad: <Target size={16} />,
  ganador: <Award size={16} />,
  perdedor: <AlertTriangle size={16} />,
  tendencia: <TrendingUp size={16} />,
};

const TYPE_COLORS = {
  riesgo: { bg: "#fee2e2", text: "#991b1b", accent: "#dc2626" },
  oportunidad: { bg: "#dbeafe", text: "#1e3a8a", accent: "#2563eb" },
  ganador: { bg: "#dcfce7", text: "#166534", accent: "#16a34a" },
  perdedor: { bg: "#fef3c7", text: "#92400e", accent: "#d97706" },
  tendencia: { bg: "#f3e8ff", text: "#6b21a8", accent: "#9333ea" },
};

export function AIInsightsPanel({ filters, endpoint = "/ai/insights", method = "post", title = "Insights con IA", subtitle = "Análisis ejecutivo generado por Claude Sonnet 4.5 sobre los datos filtrados.", storageKey = "ai_insights_collapsed" }) {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    return saved === "true";
  });
  const fetchedFor = useRef("");

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(storageKey, String(next));
      return next;
    });
  };

  const fetchInsights = async () => {
    setLoading(true);
    setError("");
    try {
      const r = method === "get"
        ? await api.get(endpoint, { params: filters })
        : await api.post(endpoint, filters);
      setInsights(r.data.insights || []);
    } catch (e) {
      setError("No se pudieron generar insights. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const key = JSON.stringify(filters);
    if (key !== fetchedFor.current && !collapsed) {
      fetchedFor.current = key;
      fetchInsights();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, collapsed]);

  // Count insights by type for the collapsed badge summary
  const typeCount = insights.reduce((acc, i) => {
    acc[i.tipo] = (acc[i.tipo] || 0) + 1;
    return acc;
  }, {});

  return (
    <section className="surface mb-5" data-testid="ai-insights-panel" style={{ background: "linear-gradient(135deg, #fffdf5 0%, #ffffff 70%)" }}>
      <div
        className="flex justify-between items-center p-6 cursor-pointer select-none"
        onClick={toggleCollapsed}
        data-testid="ai-insights-toggle"
        style={{ borderRadius: collapsed ? "var(--radius)" : "var(--radius) var(--radius) 0 0" }}
      >
        <div className="flex items-center gap-3 flex-wrap">
          <div className="kpi-icon" style={{ background: "var(--dark)", color: "var(--accent)" }}>
            <Sparkles size={18} />
          </div>
          <div>
            <h2 className="text-xl font-extrabold font-display flex items-center gap-2">
              {title}
              {collapsed && insights.length > 0 && (
                <span className="text-xs font-extrabold text-gray-500 ml-1">
                  · {insights.length} insights generados
                </span>
              )}
            </h2>
            <p className="text-sm text-gray-500 font-semibold mt-1">
              {collapsed
                ? "Haz clic para expandir el análisis de Claude Sonnet 4.5."
                : subtitle}
            </p>
          </div>
          {collapsed && insights.length > 0 && (
            <div className="flex gap-1 ml-3 flex-wrap">
              {Object.entries(typeCount).map(([t, n]) => {
                const c = TYPE_COLORS[t] || TYPE_COLORS.tendencia;
                return (
                  <span
                    key={t}
                    className="pill flex items-center gap-1"
                    style={{ background: c.bg, color: c.text, fontSize: 10 }}
                  >
                    {TYPE_ICONS[t]} {n}
                  </span>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex gap-2 items-center" onClick={(e) => e.stopPropagation()}>
          {!collapsed && (
            <button
              className="btn flex items-center gap-2"
              onClick={fetchInsights}
              disabled={loading}
              data-testid="ai-insights-regenerate"
            >
              {loading ? <span className="spinner" style={{ borderColor: "#11182733", borderTopColor: "#111827" }}></span> : <RefreshCw size={16}/>}
              Regenerar
            </button>
          )}
          <button
            className="btn flex items-center justify-center"
            onClick={toggleCollapsed}
            data-testid="ai-insights-collapse-btn"
            aria-label={collapsed ? "Expandir" : "Colapsar"}
            style={{ width: 40, padding: 0 }}
          >
            {collapsed ? <ChevronDown size={18}/> : <ChevronUp size={18}/>}
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="px-6 pb-6">
          {error && (
            <div className="p-4 rounded-xl border border-red-200 bg-red-50 text-red-800 font-semibold" data-testid="ai-insights-error">{error}</div>
          )}

          {loading && insights.length === 0 && (
            <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
              {[0,1,2,3,4,5].map((i) => (
                <div key={i} className="rounded-2xl p-4 border border-gray-200 bg-gray-50 animate-pulse" style={{ minHeight: 130 }}>
                  <div className="h-4 bg-gray-200 rounded w-1/3 mb-3"></div>
                  <div className="h-3 bg-gray-200 rounded w-full mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                </div>
              ))}
            </div>
          )}

          {!loading && insights.length === 0 && !error && (
            <div className="muted-small">Aún sin insights. Haz clic en Regenerar.</div>
          )}

          {insights.length > 0 && (
            <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" }}>
              {insights.map((ins, i) => {
                const c = TYPE_COLORS[ins.tipo] || TYPE_COLORS.tendencia;
                return (
                  <div
                    key={i}
                    className="rounded-2xl p-4 border bg-white"
                    style={{ borderColor: c.accent + "55", borderLeftWidth: 4, borderLeftColor: c.accent }}
                    data-testid={`ai-insight-${i}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span style={{ color: c.accent }}>{TYPE_ICONS[ins.tipo] || TYPE_ICONS.tendencia}</span>
                      <span className="pill" style={{ background: c.bg, color: c.text, fontSize: 10 }}>{(ins.tipo || "").toUpperCase()}</span>
                    </div>
                    <div className="font-display font-extrabold text-base leading-tight mb-2">{ins.titulo}</div>
                    <div className="text-sm text-gray-700 leading-relaxed mb-3">{ins.texto}</div>
                    {ins.metrica && (
                      <div className="text-xs font-extrabold tracking-wide" style={{ color: c.accent }}>
                        📊 {ins.metrica}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </section>
  );
}


export function AIChatWidget({ filters, endpoint = "/ai/chat", suggestions, scope = "crm", title = "Asistente CRM · Claude 4.5" }) {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput("");
    setMessages((p) => [...p, { role: "user", content: userMsg }]);
    setLoading(true);
    try {
      const r = await api.post(endpoint, { session_id: sessionId, message: userMsg, filters });
      setSessionId(r.data.session_id);
      setMessages(r.data.history.map(m => ({ role: m.role, content: m.content })));
    } catch (e) {
      setMessages((p) => [...p, { role: "assistant", content: "❌ Error al consultar a la IA. Intenta de nuevo." }]);
    } finally {
      setLoading(false);
    }
  };

  const clear = async () => {
    if (sessionId) {
      try { await api.delete(`/ai/sessions/${sessionId}`); } catch (e) { /* noop */ }
    }
    setSessionId(null);
    setMessages([]);
  };

  const defaultSuggestions = scope === "digital" ? [
    "¿Qué canal tiene la peor calidad de captura?",
    "¿Qué campañas debería pausar por bajo quality score?",
    "Resume las anomalías de captura del último mes",
    "¿Cómo mejorar el % de leads con modelo específico?",
  ] : [
    "¿Qué marcas tienen peor calificación y por qué?",
    "¿Cuál ejecutivo necesita más apoyo urgente?",
    "Resume el mes peor del año y sus causas",
    "¿Qué campaña deberíamos pausar?",
  ];
  const finalSuggestions = suggestions || defaultSuggestions;

  return (
    <>
      {!open && (
        <button
          className="fixed bottom-6 right-6 rounded-full shadow-2xl flex items-center gap-2 px-5 py-3 font-extrabold transition-transform hover:scale-105"
          style={{ background: "var(--dark)", color: "var(--accent)", zIndex: 999 }}
          onClick={() => setOpen(true)}
          data-testid="ai-chat-open"
        >
          <MessageSquare size={20}/>
          <span>Preguntar a la IA</span>
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-6 right-6 surface flex flex-col"
          style={{ width: 420, maxHeight: "80vh", zIndex: 999, boxShadow: "0 25px 50px rgba(0,0,0,.25)" }}
          data-testid="ai-chat-widget"
        >
          {/* Header */}
          <div className="flex justify-between items-center p-4 border-b border-gray-200" style={{ background: "var(--dark)", color: "white", borderRadius: "20px 20px 0 0" }}>
            <div className="flex items-center gap-2">
              <Sparkles size={18} style={{ color: "var(--accent)" }}/>
              <strong className="text-sm">{title}</strong>
            </div>
            <div className="flex gap-2">
              {messages.length > 0 && (
                <button onClick={clear} title="Nueva conversación" className="text-gray-300 hover:text-white" data-testid="ai-chat-clear">
                  <Trash2 size={16}/>
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-gray-300 hover:text-white" data-testid="ai-chat-close">
                <X size={18}/>
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 flex flex-col gap-3" style={{ background: "#fafafa", minHeight: 280 }} data-testid="ai-chat-messages">
            {messages.length === 0 && !loading && (
              <div className="flex flex-col gap-3">
                <div className="text-sm text-gray-600 font-semibold">
                  👋 Hola, soy tu asistente CRM. Puedo analizar los datos filtrados actuales. Prueba con:
                </div>
                {finalSuggestions.map((q) => (
                  <button
                    key={q}
                    onClick={() => setInput(q)}
                    className="text-left text-xs font-semibold p-3 rounded-lg bg-white border border-gray-200 hover:border-yellow-400 hover:bg-yellow-50 transition"
                    data-testid={`ai-chat-suggestion-${q.substring(0, 15)}`}
                  >
                    💬 {q}
                  </button>
                ))}
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                className={"max-w-[90%] " + (m.role === "user" ? "self-end" : "self-start")}
              >
                <div
                  className="px-3 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap"
                  style={
                    m.role === "user"
                      ? { background: "var(--dark)", color: "white", borderBottomRightRadius: 4 }
                      : { background: "white", border: "1px solid var(--line)", color: "var(--dark)", borderBottomLeftRadius: 4 }
                  }
                >{m.content}</div>
              </div>
            ))}
            {loading && (
              <div className="self-start max-w-[90%]">
                <div className="px-3 py-3 rounded-2xl text-sm bg-white border border-gray-200 flex items-center gap-2">
                  <div className="flex gap-1">
                    {[0,1,2].map((i) => (
                      <div key={i} className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" style={{ animationDelay: `${i*0.15}s` }}></div>
                    ))}
                  </div>
                  <span className="text-xs text-gray-500 font-semibold">Analizando datos…</span>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-3 border-t border-gray-200 flex gap-2" style={{ background: "white", borderRadius: "0 0 20px 20px" }}>
            <input
              type="text"
              className="text-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Pregunta algo sobre tus datos…"
              disabled={loading}
              data-testid="ai-chat-input"
            />
            <button
              className="btn primary flex items-center justify-center"
              onClick={send}
              disabled={!input.trim() || loading}
              data-testid="ai-chat-send"
              style={{ width: 44, padding: 0 }}
            >
              <Send size={16}/>
            </button>
          </div>
        </div>
      )}
    </>
  );
}


// --- Paginated Unmanaged table component ---
// Normalise a phone to E.164 for wa.me. Handles DR local formats (10 digits
// starting with 8 = 1-DR area code). If already has +/1, use as-is.
const _digitsOnly = (s) => String(s || "").replace(/\D/g, "");
const _waNumber = (raw) => {
  const d = _digitsOnly(raw);
  if (!d) return "";
  if (d.length === 10 && d.startsWith("8")) return "1" + d;   // DR local → +1 809/829/849
  if (d.length === 11 && d.startsWith("1")) return d;         // already +1
  return d;
};
const _waMessage = (r) => {
  const name = (r.name || "").split(" ")[0] || "";
  const brand = r.brand || "";
  const model = r.model || "";
  const vehicle = [brand, model].filter(Boolean).join(" ");
  const greeting = name ? `Hola ${name}, ` : "Hola, ";
  const context = vehicle ? `te contacto por tu interés en el ${vehicle}. ` : "te contacto de parte de nuestro equipo comercial. ";
  return `${greeting}soy del equipo comercial de Santo Domingo Motors. ${context}¿Cuándo podemos coordinar una visita al showroom para mostrarte todos los detalles?`;
};

export function UnmanagedPaginated({ filters }) {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [density, setDensity] = useState(() => localStorage.getItem("unmanaged_density") || "compact");

  useEffect(() => { localStorage.setItem("unmanaged_density", density); }, [density]);

  const params = { ...filters, page, page_size: pageSize };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/leads/unmanaged-paginated", { params });
      setData(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, pageSize, JSON.stringify(filters)]);
  useEffect(() => { setPage(1); }, [JSON.stringify(filters)]);

  const download = async (format) => {
    const res = await api.get("/leads/unmanaged-export", {
      params: { ...filters, format },
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads_sin_gestionar.${format === "xlsx" ? "xlsx" : "csv"}`;
    a.click();
  };

  const fmt = (n) => Number(n || 0).toLocaleString("es-DO");
  const pages = data.pages || 1;
  const isCompact = density === "compact";

  // Build page button list with ellipsis
  const buildPages = () => {
    if (pages <= 7) return [...Array(pages)].map((_, i) => i + 1);
    if (page <= 4) return [1, 2, 3, 4, 5, "…", pages];
    if (page >= pages - 3) return [1, "…", pages - 4, pages - 3, pages - 2, pages - 1, pages];
    return [1, "…", page - 1, page, page + 1, "…", pages];
  };

  return (
    <div data-testid="unmanaged-paginated">
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <p className="text-sm text-gray-500 font-semibold">
          <strong>{fmt(data.total)}</strong> leads sin gestionar · Página {data.page} de {pages}
        </p>
        <div className="flex gap-2 items-center">
          <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden" role="group" aria-label="Densidad de tabla">
            <button
              type="button"
              className="px-2 py-1 text-xs font-extrabold flex items-center gap-1 transition"
              style={{ background: isCompact ? "#111827" : "#fff", color: isCompact ? "#f4c400" : "#6b7280", height: 30 }}
              onClick={() => setDensity("compact")}
              data-testid="unmanaged-density-compact"
              title="Vista compacta (más filas visibles)"
            ><Rows3 size={12}/> Compacta</button>
            <button
              type="button"
              className="px-2 py-1 text-xs font-extrabold flex items-center gap-1 transition"
              style={{ background: !isCompact ? "#111827" : "#fff", color: !isCompact ? "#f4c400" : "#6b7280", height: 30, borderLeft: "1px solid #e5e7eb" }}
              onClick={() => setDensity("detailed")}
              data-testid="unmanaged-density-detailed"
              title="Vista detallada (nombres completos, texto grande)"
            ><Rows2 size={12}/> Detallada</button>
          </div>
          <select
            className="select-input"
            style={{ width: 90, height: 30, fontSize: 12 }}
            value={pageSize}
            onChange={(e) => { setPageSize(parseInt(e.target.value)); setPage(1); }}
            data-testid="unmanaged-page-size"
          >
            {[25, 50, 100, 200].map((s) => <option key={s} value={s}>{s}/pág</option>)}
          </select>
          <button className="btn" style={{ height: 30, fontSize: 12, padding: "0 10px" }} onClick={() => download("csv")} data-testid="unmanaged-export-csv">CSV</button>
          <button className="btn accent" style={{ height: 30, fontSize: 12, padding: "0 10px" }} onClick={() => download("xlsx")} data-testid="unmanaged-export-xlsx">Excel</button>
        </div>
      </div>

      <div style={{ overflowX: "auto", opacity: loading ? 0.5 : 1, transition: ".15s" }}>
        <table className={isCompact ? "compact-table" : ""} data-testid="unmanaged-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Nombre</th>
              <th>Marca</th>
              <th>Modelo</th>
              <th>Unidad de Negocio</th>
              <th>Ejecutivo</th>
              <th>Origen</th>
              <th>Razón</th>
              <th>Teléfono</th>
              <th style={{ textAlign: "center", width: 90 }}>Contactar</th>
            </tr>
          </thead>
          <tbody>
            {data.items.length ? data.items.map((r) => {
              const wa = _waNumber(r.phone);
              const canWA = wa.length >= 10;
              const nowrap = isCompact ? { whiteSpace: "nowrap" } : {};
              const truncate = (max) => isCompact ? {
                maxWidth: max, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"
              } : {};
              return (
                <tr key={r.id}>
                  <td className="text-gray-500" style={nowrap}>{r.created_at ? r.created_at.slice(0, 10) : "—"}</td>
                  <td className="font-bold" style={nowrap}>{r.name}</td>
                  <td>{r.brand}</td>
                  <td className="text-gray-600" style={truncate(200)} title={r.model}>{r.model}</td>
                  <td className="text-gray-600" style={nowrap}>{r.branch}</td>
                  <td className="text-gray-600" style={truncate(180)} title={r.executive}>{r.executive}</td>
                  <td className="text-gray-600" style={nowrap}>{r.origin}</td>
                  <td><span className="pill warn" style={isCompact ? { fontSize: 10, padding: "2px 6px" } : {}}>{r.status_reason}</span></td>
                  <td className="text-gray-500" style={{ ...nowrap, fontVariantNumeric: "tabular-nums" }}>{r.phone}</td>
                  <td style={{ textAlign: "center" }}>
                    {canWA ? (
                      <a
                        href={`https://wa.me/${wa}?text=${encodeURIComponent(_waMessage(r))}`}
                        target="_blank"
                        rel="noreferrer noopener"
                        title={`Enviar WhatsApp a ${r.name}`}
                        data-testid={`wa-btn-${r.id}`}
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 4,
                          background: "#25d366", color: "#fff",
                          padding: isCompact ? "3px 8px" : "5px 10px",
                          borderRadius: 6, fontSize: isCompact ? 10 : 11, fontWeight: 800,
                          textDecoration: "none", lineHeight: 1,
                        }}
                      >
                        <MessageSquare size={isCompact ? 10 : 12}/> WhatsApp
                      </a>
                    ) : <span className="text-gray-300 text-xs">—</span>}
                  </td>
                </tr>
              );
            }) : (
              <tr><td colSpan="10" className="muted-small text-center py-6">Sin leads pendientes en los filtros actuales.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex justify-center items-center gap-1 mt-4 flex-wrap" data-testid="unmanaged-pagination">
          <button
            className="btn"
            style={{ height: 30, padding: "0 8px", fontSize: 12 }}
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            data-testid="unmanaged-prev-page"
          >‹</button>
          {buildPages().map((p, idx) => p === "…" ? (
            <span key={`e${idx}`} className="px-2 text-gray-400 font-bold text-xs">…</span>
          ) : (
            <button
              key={p}
              className={"btn" + (p === page ? " primary" : "")}
              style={{ height: 30, padding: "0 10px", minWidth: 32, fontSize: 12 }}
              onClick={() => setPage(p)}
              data-testid={`unmanaged-page-${p}`}
            >{p}</button>
          ))}
          <button
            className="btn"
            style={{ height: 30, padding: "0 8px", fontSize: 12 }}
            disabled={page >= pages}
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            data-testid="unmanaged-next-page"
          >›</button>
        </div>
      )}
    </div>
  );
}
